import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { isNotNullish } from '@bcef/ia-shared';
import { Store } from '@ngrx/store';
import { Observable, take } from 'rxjs';

import {
  FormStep,
  RelationShipWithDeceased,
  RoutesPath,
  YesNoIDK,
} from '@features/succession/enums';
import {
  InheritanceInformationForm,
  InheritanceInformationFormGroup,
} from '@features/succession/forms';
import {
  CustomerInformation,
  Inheritance,
  InheritanceInformation,
  ListBoxItem,
} from '@features/succession/models';
import { selectCountries, selectGenders, selectShowSpouse } from '@features/succession/store';
import { SuccessionFacade } from '@features/succession/succession.facade';
import { ControlValidators } from '@shared/validators/control.validators';

@Injectable({ providedIn: 'root' })
export class InheritanceInformationFacade {
  protected destroyRef = inject(DestroyRef);

  private readonly successionFacade = inject(SuccessionFacade);

  private readonly formBuilder = inject(FormBuilder);

  private readonly store = inject(Store);

  public inheritanceInformation: InheritanceInformation | undefined;

  public inheritanceInformationForm: FormGroup<InheritanceInformationForm> = this.formBuilder.group(
    {
      spouse: this.buildInheritanceInformationFormGroup(),
      hasChildren: new FormControl('', { validators: [Validators.required], nonNullable: true }),
      children: this.formBuilder.array([]),
    },
  );

  public genders$: Observable<ListBoxItem[]> = this.store
    .select(selectGenders)
    .pipe(isNotNullish());

  public countries$: Observable<ListBoxItem[]> = this.store
    .select(selectCountries)
    .pipe(isNotNullish());

  public showSpouse$: Observable<boolean> = this.store.select(selectShowSpouse);

  constructor() {
    this.resetInheritanceInformationFormValues();

    this.hasChildren.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (value !== YesNoIDK.YES) {
        this.children.clear();
      }
    });
  }

  public get spouse(): FormGroup<InheritanceInformationFormGroup> {
    return this.inheritanceInformationForm.controls.spouse;
  }

  public get hasChildren(): FormControl<string> {
    return this.inheritanceInformationForm.controls.hasChildren;
  }

  public get children(): FormArray {
    return this.inheritanceInformationForm.controls.children;
  }

  public resetInheritanceInformationFormValues(): void {
    this.successionFacade.customerInformation$
      .pipe(take(1))
      .subscribe((customerInformation: CustomerInformation) => {
        this.inheritanceInformation = customerInformation.inheritanceInformations;

        if (this.inheritanceInformation?.hasSpouse === true) {
          this.spouse.reset(this.inheritanceInformation.spouse);

          if (this.inheritanceInformation?.readOnlySpouse === true) {
            this.spouse.disable();
          }
        }

        if (this.inheritanceInformation?.hasChild) {
          this.hasChildren.patchValue(this.inheritanceInformation?.hasChild);

          if (this.inheritanceInformation?.children.length) {
            [...this.inheritanceInformation?.children]
              .sort((a: Inheritance, b: Inheritance) => {
                if (!a.id || !b.id) {
                  return 0;
                }
                return Number(a.id) - Number(b.id);
              })
              .forEach((child) => {
                this.addChild(child);
              });

            if (this.inheritanceInformation.readOnlyFirstChild) {
              this.hasChildren.disable();
              this.inheritanceInformationForm.controls.children.at(0).disable();
            }
          }
        }
      });
  }

  private buildInheritanceInformationFormGroup(
    heir?: Inheritance,
  ): FormGroup<InheritanceInformationFormGroup> {
    return new FormGroup<InheritanceInformationFormGroup>(
      {
        id: new FormControl(heir?.id || null),
        gender: new FormControl(heir?.gender || ''),
        name: new FormControl(heir?.name || ''),
        firstname: new FormControl(heir?.firstname || ''),
        email: new FormControl(heir?.email || '', [
          ControlValidators.emailFormatValidator(),
          ControlValidators.noSpecialCharactersValidator(),
        ]),
        phoneNumber: new FormControl(heir?.phoneNumber || ''),
        zipcode: new FormControl(heir?.zipcode || ''),
        complementaryAddress: new FormControl(heir?.complementaryAddress || ''),
        address: new FormControl(heir?.address || ''),
        city: new FormControl(heir?.city || ''),
        country: new FormControl(heir?.country || ''),
      },
      [ControlValidators.requiredIfOneHasValue(['gender', 'name', 'firstname'])],
    );
  }

  public addChild(child?: Inheritance): void {
    this.children.push(this.buildInheritanceInformationFormGroup(child));
  }

  public removeChild(index: number): void {
    this.children.removeAt(index);
  }

  public getChildFormGroup(index: number): FormGroup {
    return this.inheritanceInformationForm.controls.children.at(index) as FormGroup;
  }

  public navigateToPage(route: RoutesPath): void {
    const inheritanceInformation: InheritanceInformation = {
      children:
        this.hasChildren.value === YesNoIDK.YES
          ? this.children.getRawValue().map((item) => ({
              ...item,
              relationshipWithDeceased: RelationShipWithDeceased.CHILD,
            }))
          : [],
      hasChild: this.hasChildren.value,
      hasSpouse: this.inheritanceInformation?.hasSpouse || false,
      readOnlySpouse: this.inheritanceInformation?.readOnlySpouse || false,
      showSpouse: this.inheritanceInformation?.showSpouse || false,
      readOnlyFirstChild: this.inheritanceInformation?.readOnlyFirstChild || false,
      spouse: {
        gender: this.spouse.controls.gender.value || '',
        name: this.spouse.controls.name.value,
        firstname: this.spouse.controls.firstname.value || '',
        email: this.spouse.controls.email.value || '',
        phoneNumber: this.spouse.controls.phoneNumber.value || '',
        zipcode: this.spouse.controls.zipcode.value || '',
        complementaryAddress: this.spouse.controls.complementaryAddress.value || '',
        address: this.spouse.controls.address.value || '',
        city: this.spouse.controls.city.value || '',
        country: this.spouse.controls.country.value || '',
        relationshipWithDeceased: RelationShipWithDeceased.SPOUSE,
      },
    };

    const customerInformation: CustomerInformation = {
      token: this.successionFacade.token,
      savePage: FormStep.FIVE,
      loadPage: route === RoutesPath.PAGE_4 ? FormStep.FOUR : FormStep.SIX,
      inheritanceInformations: inheritanceInformation,
      deceasedInformations: undefined,
      deceasedMoreInformations: undefined,
      reporterInformations: undefined,
      notaryInformations: undefined,
    };

    this.successionFacade.saveAndLoad(customerInformation);
  }

  public onPrevious(): void {
    this.spouse.enable();
    this.navigateToPage(RoutesPath.PAGE_4);
    this.successionFacade.trackPrevious();
  }

  public onNext(): void {
    this.inheritanceInformationForm.markAllAsTouched();
    if (this.inheritanceInformationForm.invalid) {
      return;
    }

    this.navigateToPage(RoutesPath.PAGE_6);
    this.successionFacade.trackNext();
  }
}
